import { Component, inject, signal } from '@angular/core'
import { SwUpdate } from '@angular/service-worker'
import { environment } from '../../../environments/environment'

/**
 * Numerito de versión + botón "revisar actualización", visible en el
 * dashboard y en el móvil (2026-09-10). Nace del incidente de fotos con URL
 * vieja: sin esto, la única forma de saber si un dispositivo quedó corriendo
 * código desactualizado era deducirlo indirectamente desde Supabase (formato
 * de la URL guardada) — con esto, un vistazo a la pantalla alcanza.
 *
 * No reemplaza ni toca el mecanismo de auto-actualización seguro que ya
 * existe en app.ts (programarRecarga/hayTrabajoSinGuardar) — este botón solo
 * dispara `checkForUpdate()`, que únicamente pregunta si hay algo nuevo. Si
 * lo hay, el flujo de activación + recarga segura de siempre sigue su curso
 * solo, sin saltarse ninguna de sus protecciones.
 */
@Component({
  selector: 'app-indicador-version',
  standalone: true,
  template: `
    <div class="indicador-version">
      <span class="indicador-version-numero">v{{ version }}</span>
      <button type="button" class="indicador-version-boton" [disabled]="revisando()" (click)="revisar()">
        {{ revisando() ? 'Revisando…' : mensaje() || 'Revisar actualización' }}
      </button>
    </div>
  `,
  styles: [
    `
      /*
       * Usa currentColor en vez de un color fijo para heredar el tono de
       * donde se ponga (blanco en el header sage del movil, oscuro en el
       * sidebar claro del dashboard) — así se ve bien en los dos lugares
       * sin necesitar una variante distinta por pantalla.
       */
      .indicador-version {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        margin-top: 3px;
        padding: 2px 8px 2px 7px;
        border-radius: 999px;
        background: color-mix(in srgb, currentColor 16%, transparent);
        font-size: 10.5px;
        line-height: 1.5;
      }
      .indicador-version-numero {
        font-family: ui-monospace, monospace;
        font-weight: 700;
        opacity: 0.95;
      }
      .indicador-version-boton {
        background: none;
        border: none;
        padding: 0;
        font: inherit;
        color: inherit;
        opacity: 0.8;
        text-decoration: underline;
        cursor: pointer;
      }
      .indicador-version-boton:hover:not(:disabled) {
        opacity: 1;
      }
      .indicador-version-boton:disabled {
        cursor: default;
        text-decoration: none;
      }
    `,
  ],
})
export class IndicadorVersionComponent {
  private readonly swUpdate = inject(SwUpdate, { optional: true })

  readonly version = environment.appVersion || '0.0.0'
  readonly revisando = signal(false)
  readonly mensaje = signal('')

  async revisar(): Promise<void> {
    if (!this.swUpdate?.isEnabled) {
      this.mensaje.set('Sin Service Worker en este ambiente')
      return
    }
    this.revisando.set(true)
    this.mensaje.set('')
    try {
      const hayVersionNueva = await this.swUpdate.checkForUpdate()
      // Si hay una version nueva, el propio app.ts ya esta suscrito a
      // versionUpdates y va a activarla + recargar solo cuando sea seguro
      // (ver programarRecarga) — este boton no se salta esa espera.
      this.mensaje.set(hayVersionNueva ? '↻ Hay una versión nueva, se actualizará sola' : '✓ Ya tienes la última versión')
    } catch {
      this.mensaje.set('No se pudo revisar (sin conexión?)')
    } finally {
      this.revisando.set(false)
      setTimeout(() => this.mensaje.set(''), 6000)
    }
  }
}
